/**
 * Public candidate-facing onboarding form.
 *
 * Reached via `/onboarding/:token` — this page is unauthenticated, the token
 * IS the auth. Data lives in one Mongo `onboarding_forms` doc, uploaded docs
 * live in Emergent Object Storage.
 *
 * UX rules (per user brief):
 *  - Ultra-design, mobile-first, sections repliables, sauvegarde auto (5s debounce).
 *  - Aucune friction : chaque section montre son état de complétion en live.
 *  - Un seul bouton final "Envoyer mon dossier" — activé quand tout est OK.
 */
import { AnimatePresence, motion } from "framer-motion";
import {
    AlertCircle,
    Banknote,
    Check,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    FileText,
    Home,
    IdCard,
    Image,
    Loader2,
    Lock,
    Mail,
    MapPin,
    Phone,
    Plus,
    Save,
    Send,
    ShieldCheck,
    Sparkles,
    Trash2,
    Upload,
    UserRound,
    Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { isValidBic, isValidIban } from "../lib/bank_validators";
import { fieldErrors } from "../lib/onboardingValidators";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/public/onboarding`;


/* ─────────────────────────── Document list definition */

const DOCS = [
    { key: "rib", label: "RIB", hint: "Relevé d'identité bancaire", required: true, Icon: FileText },
    { key: "identity", label: "Pièce d'identité", hint: "CNI recto-verso ou titre de séjour", required: true, Icon: IdCard },
    { key: "vitale", label: "Carte Vitale", hint: "Attestation + carte Vitale", required: true, Icon: ShieldCheck },
    { key: "address_proof", label: "Justificatif de domicile", hint: "Moins de 3 mois", required: true, Icon: Home },
    { key: "badge_photo", label: "Photo pour le badge", hint: "Portrait, fond neutre — min 400×500 px, max 10 Mo", required: true, Icon: Image },
    { key: "handicap", label: "Reconnaissance handicap (RQTH)", hint: "Optionnel", required: false, Icon: FileText },
    { key: "student_card", label: "Carte étudiante", hint: "Si vous êtes étudiant(e)", required: false, Icon: FileText },
    { key: "hosting_attestation", label: "Attestation d'hébergement", hint: "Si vous êtes hébergé(e)", required: false, Icon: FileText },
    { key: "hosting_host_id", label: "Pièce d'identité de l'hébergeur", hint: "Si vous êtes hébergé(e)", required: false, Icon: IdCard },
];

const REQUIRED_FIELDS = [
    "civilite", "last_name", "first_name", "birth_date", "nationality",
    "social_security_number", "birth_city", "birth_dept",
    "address_line1", "address_city",
    "address_postal_code", "phone_mobile", "email",
    "iban", "bic",
];


/* ─────────────────────────── Section helpers */

function Section({ title, icon: Icon, complete, defaultOpen = true, children, testId }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <section
            data-testid={testId}
            className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-5 md:px-7 py-5 hover:bg-slate-50/60 transition text-left"
                data-testid={`${testId}-toggle`}
            >
                <div className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${
                    complete ? "bg-emerald-100 text-emerald-700" : "bg-shariti-orange-soft text-shariti-orange"
                }`}>
                    {complete ? <Check className="h-5 w-5" strokeWidth={3} /> : Icon ? <Icon className="h-5 w-5" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[16px] md:text-lg font-black text-shariti-black tracking-tight">{title}</div>
                    <div className={`text-[11px] font-bold uppercase tracking-widest mt-0.5 ${
                        complete ? "text-emerald-700" : "text-slate-400"
                    }`}>
                        {complete ? "Complété" : "À compléter"}
                    </div>
                </div>
                {open ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 md:px-7 pb-6 pt-1 border-t border-slate-100">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testId, locked, cols = 12, error }) {
    // cols is the sm+ span (out of 12). Below sm, everything stacks.
    // colClass is defined statically so Tailwind picks it up from JIT scan.
    const colClass = {
        3: "sm:col-span-6 lg:col-span-3",
        4: "sm:col-span-6 lg:col-span-4",
        6: "sm:col-span-6",
        8: "sm:col-span-12 lg:col-span-8",
        12: "sm:col-span-12",
    }[cols] || "sm:col-span-12";
    // Date fields → dedicated 3-select picker (day / month / year). The
    // native <input type="date"> forces users to click month-by-month
    // to reach a 1987 or 1990 birth year on mobile — chronophage. A
    // three-select picker lets them jump instantly to any decade.
    if (type === "date") {
        // Year window heuristic:
        //  - "expiration / échéance / expiry" → future dates → today+30y down to today-5y
        //  - anything else → assumed to be a birth date → today back to today-100y
        const lower = String(label || "").toLowerCase();
        const isFutureDate = /expir|échéance|echeance|expiry|fin de|end date|date de fin/.test(lower);
        const now = new Date();
        const [yMin, yMax] = isFutureDate
            ? [now.getFullYear() - 5, now.getFullYear() + 30]
            : [now.getFullYear() - 100, now.getFullYear()];
        return (
            <label className={`block ${colClass}`}>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                    {label}{required && <span className="text-shariti-orange ml-1">*</span>}
                </span>
                <DatePartsField
                    value={value ?? ""}
                    onChange={onChange}
                    disabled={locked}
                    testId={testId}
                    yearMin={yMin}
                    yearMax={yMax}
                    yearOrder={isFutureDate ? "asc" : "desc"}
                />
            </label>
        );
    }
    return (
        <label className={`block ${colClass}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                {label}{required && <span className="text-shariti-orange ml-1">*</span>}
            </span>
            <input
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                disabled={locked}
                aria-invalid={!!error}
                className={`w-full rounded-2xl border-2 ${error ? "border-red-400 bg-red-50/40" : "border-slate-200 bg-white"} px-4 py-3 text-sm font-semibold text-shariti-black placeholder:text-slate-300 focus:border-shariti-orange focus:outline-none disabled:opacity-60 disabled:bg-slate-50 transition`}
            />
            {error && !locked && (
                <span className="mt-1 block text-[11px] font-bold text-red-600" data-testid={`${testId}-error`}>{error}</span>
            )}
        </label>
    );
}

// ---------------- Custom date picker (day / month / year selects) ----------------
const MONTH_LABELS = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function DatePartsField({ value, onChange, disabled, testId, yearMin, yearMax, yearOrder = "desc" }) {
    // value is an ISO "YYYY-MM-DD" string (matches the backend contract).
    // Local state keeps track of partial selections (e.g. Day + Month set
    // but Year still pending) so we never wipe the UI while the user is
    // mid-selection. We ONLY push a value to the parent once the 3 parts
    // are set; if any part is cleared the parent goes back to "".
    const [parts, setParts] = useState(() => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
        return m ? { y: m[1], mo: m[2], d: m[3] } : { y: "", mo: "", d: "" };
    });

    // Sync from parent when it changes externally (e.g. hydrated from API).
    useEffect(() => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
        const next = m ? { y: m[1], mo: m[2], d: m[3] } : { y: "", mo: "", d: "" };
        setParts((cur) => (cur.y === next.y && cur.mo === next.mo && cur.d === next.d ? cur : next));
    }, [value]);

    const commit = (next) => {
        setParts(next);
        const { y, mo, d } = next;
        if (y && mo && d) {
            onChange(`${y}-${mo}-${d}`);
        } else if (value) {
            // Only clear the parent if it currently holds a value.
            onChange("");
        }
    };

    const years = [];
    if (yearOrder === "asc") {
        for (let y = yearMin; y <= yearMax; y += 1) years.push(y);
    } else {
        for (let y = yearMax; y >= yearMin; y -= 1) years.push(y);
    }

    // Days depend on the currently selected month/year (e.g. Feb 2024 = 29 days).
    const daysInMonth = (() => {
        const yn = Number(parts.y);
        const mn = Number(parts.mo);
        if (!yn || !mn) return 31;
        return new Date(yn, mn, 0).getDate();
    })();

    const selectCls =
        "flex-1 rounded-2xl border-2 border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-shariti-black focus:border-shariti-orange focus:outline-none disabled:opacity-60 disabled:bg-slate-50 transition min-w-0";

    return (
        <div className="flex items-stretch gap-2" data-testid={testId}>
            <select
                value={parts.d}
                onChange={(e) => commit({ ...parts, d: e.target.value })}
                disabled={disabled}
                aria-label="Jour"
                data-testid={testId ? `${testId}-day` : undefined}
                className={selectCls}
            >
                <option value="">Jour</option>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={String(d).padStart(2, "0")}>{d}</option>
                ))}
            </select>
            <select
                value={parts.mo}
                onChange={(e) => {
                    const nextMo = e.target.value;
                    // Clamp day to the new month's length so we never emit
                    // "2024-02-31".
                    let nextD = parts.d;
                    if (nextD && nextMo && parts.y) {
                        const max = new Date(Number(parts.y), Number(nextMo), 0).getDate();
                        if (Number(nextD) > max) nextD = String(max).padStart(2, "0");
                    }
                    commit({ ...parts, mo: nextMo, d: nextD });
                }}
                disabled={disabled}
                aria-label="Mois"
                data-testid={testId ? `${testId}-month` : undefined}
                className={`${selectCls} min-w-[7rem]`}
            >
                <option value="">Mois</option>
                {MONTH_LABELS.map((m, i) => (
                    <option key={i} value={String(i + 1).padStart(2, "0")}>{m}</option>
                ))}
            </select>
            <select
                value={parts.y}
                onChange={(e) => commit({ ...parts, y: e.target.value })}
                disabled={disabled}
                aria-label="Année"
                data-testid={testId ? `${testId}-year` : undefined}
                className={selectCls}
            >
                <option value="">Année</option>
                {years.map((y) => (
                    <option key={y} value={String(y)}>{y}</option>
                ))}
            </select>
        </div>
    );
}

function Select({ label, value, onChange, options, required, testId, locked, cols = 12 }) {
    const colClass = {
        2: "sm:col-span-6 lg:col-span-2",
        3: "sm:col-span-6 lg:col-span-3",
        4: "sm:col-span-6 lg:col-span-4",
        6: "sm:col-span-6",
        12: "sm:col-span-12",
    }[cols] || "sm:col-span-12";
    return (
        <label className={`block ${colClass}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                {label}{required && <span className="text-shariti-orange ml-1">*</span>}
            </span>
            <select
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                disabled={locked}
                className="w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-shariti-black focus:border-shariti-orange focus:outline-none disabled:opacity-60 disabled:bg-slate-50 transition"
            >
                <option value="">—</option>
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}


// Live validity hint used under the IBAN + BIC inputs. Silent while the
// field is empty (avoids scaring the candidate before they've even
// started typing). Once a value is present it flashes green ✔ or amber
// ⚠ so the candidate gets instant reassurance / warning WITHOUT waiting
// for the server-side submit check.
function BankValidityHint({ value, validate, labelOk, labelKo, testId }) {
    const raw = (value ?? "").trim();
    if (!raw) return null;
    const ok = validate(raw);
    if (ok) {
        return (
            <div
                className="inline-flex items-center gap-1.5 text-[11px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
                data-testid={testId}
                data-valid="true"
            >
                <Check className="h-3 w-3" strokeWidth={3} />
                {labelOk}
            </div>
        );
    }
    return (
        <div
            className="inline-flex items-center gap-1.5 text-[11px] font-black text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1"
            data-testid={testId}
            data-valid="false"
        >
            <AlertCircle className="h-3 w-3" />
            {labelKo}
        </div>
    );
}


/* ─────────────────────────── Document card */

const BADGE_PHOTO_MIN_WIDTH = 400;
const BADGE_PHOTO_MIN_HEIGHT = 500;

/**
 * Lit les dimensions d'une image côté navigateur.
 * Renvoie `null` si le format n'est pas décodable par le navigateur (ex: HEIC
 * sur Chrome/Firefox) — dans ce cas on laisse le backend faire la validation
 * autoritative après conversion JPEG.
 */
function readImageDimensions(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null); // HEIC / format inconnu → délégation backend
        };
        img.src = url;
    });
}

function DocRow({ token, doc, uploaded, onUploaded, onRemoved, locked }) {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);
    const Icon = doc.Icon;

    async function pickFile(file) {
        if (!file) return;
        setError("");

        // 1) Poids max — check universel
        if (file.size > 10 * 1024 * 1024) {
            const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
            setError(`Fichier trop lourd (${sizeMb} Mo). Maximum 10 Mo. Compressez la photo ou choisissez un autre fichier.`);
            return;
        }

        // 2) Badge photo — validation dimensions + format image
        if (doc.key === "badge_photo") {
            const isImage = (file.type || "").startsWith("image/");
            if (!isImage) {
                setError("La photo badge doit être une image (JPEG, PNG ou HEIC). Les PDF ne sont pas acceptés.");
                return;
            }
            const dims = await readImageDimensions(file);
            if (dims && (dims.width < BADGE_PHOTO_MIN_WIDTH || dims.height < BADGE_PHOTO_MIN_HEIGHT)) {
                setError(
                    `Photo trop petite (${dims.width}×${dims.height} px). Minimum ${BADGE_PHOTO_MIN_WIDTH}×${BADGE_PHOTO_MIN_HEIGHT} px en portrait. Prenez une nouvelle photo en meilleure qualité.`
                );
                return;
            }
            // dims === null → HEIC iPhone, le backend valide après conversion JPEG
        }

        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const res = await fetch(`${API}/${token}/upload/${doc.key}`, {
                method: "POST",
                body: form,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                setError(err.detail || "Erreur d'envoi. Réessayez.");
                return;
            }
            const data = await res.json();
            onUploaded(doc.key, data.document);
        } finally {
            setUploading(false);
        }
    }

    async function remove() {
        if (!confirm("Supprimer ce document ?")) return;
        setError("");
        const res = await fetch(`${API}/${token}/upload/${doc.key}`, { method: "DELETE" });
        if (res.ok) onRemoved(doc.key);
    }

    return (
        <div
            className={`rounded-2xl border-2 p-4 transition ${
                error
                    ? "border-red-300 bg-red-50/60"
                    : uploaded ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50/40"
            }`}
            data-testid={`doc-row-${doc.key}`}
        >
            <div className="flex items-start gap-3">
                <div className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${
                    uploaded ? "bg-emerald-500 text-white" : "bg-white text-shariti-orange border-2 border-slate-200"
                }`}>
                    {uploaded ? <Check className="h-5 w-5" strokeWidth={3} /> : <Icon className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-extrabold text-shariti-black">{doc.label}</div>
                        {doc.required
                            ? <span className="text-[9px] font-bold uppercase tracking-widest text-shariti-orange bg-shariti-orange-soft px-1.5 py-0.5 rounded">Obligatoire</span>
                            : <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Optionnel</span>
                        }
                    </div>
                    <div className="text-[11px] font-medium text-slate-500 mt-0.5">{doc.hint}</div>
                    {uploaded && (
                        <div className="mt-1.5 text-[11px] font-semibold text-emerald-700 truncate">
                            {uploaded.original_filename} · {(uploaded.size / 1024).toFixed(0)} Ko
                        </div>
                    )}
                </div>
                {!locked && (
                    <div className="flex flex-col gap-1.5 items-end shrink-0">
                        <input
                            ref={inputRef}
                            type="file"
                            accept={doc.key === "badge_photo" ? "image/*,.heic,.heif" : "image/*,application/pdf"}
                            className="hidden"
                            onChange={(e) => pickFile(e.target.files?.[0])}
                            data-testid={`doc-file-${doc.key}`}
                        />
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            data-testid={`doc-upload-${doc.key}`}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-8 text-[11px] font-black uppercase tracking-widest transition ${
                                uploaded
                                    ? "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                    : "bg-shariti-black text-white hover:bg-shariti-orange"
                            }`}
                        >
                            {uploading
                                ? <><Loader2 className="h-3 w-3 animate-spin" /> Envoi</>
                                : uploaded ? "Remplacer" : <><Upload className="h-3 w-3" /> Ajouter</>
                            }
                        </button>
                        {uploaded && (
                            <button
                                type="button"
                                onClick={remove}
                                data-testid={`doc-remove-${doc.key}`}
                                className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 hover:underline"
                            >
                                <Trash2 className="h-3 w-3" /> Retirer
                            </button>
                        )}
                    </div>
                )}
            </div>
            {error && (
                <div
                    className="mt-3 rounded-xl border border-red-300 bg-white px-3 py-2 text-[11px] font-semibold leading-relaxed text-red-700 flex items-start gap-2"
                    data-testid={`doc-error-${doc.key}`}
                    role="alert"
                >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
}


/* ─────────────────────────── Main page */

export default function OnboardingForm() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [record, setRecord] = useState(null); // {status, data, documents, candidate_*, is_locked}
    const [data, setData] = useState({});
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const debounceRef = useRef(null);

    // -------- initial load (with 10s timeout so the loader never sticks
    // forever on a flaky network — the candidate gets a clear error banner
    // and can retry manually)
    const [loadError, setLoadError] = useState(null);
    const load = useCallback(async () => {
        setLoadError(null);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        try {
            const res = await fetch(`${API}/${token}`, { signal: controller.signal });
            clearTimeout(timeoutId);
            if (!res.ok) {
                if (res.status === 404) {
                    setRecord({ error: true });
                } else {
                    setLoadError(`Le serveur a répondu ${res.status}. Réessayez dans un instant.`);
                }
                setLoading(false);
                return;
            }
            const d = await res.json();
            setRecord(d);
            setData(d.data || {});
        } catch (e) {
            clearTimeout(timeoutId);
            setLoadError(
                e.name === "AbortError"
                    ? "Le serveur met trop de temps à répondre. Vérifiez votre connexion et réessayez."
                    : "Impossible de contacter le serveur. Vérifiez votre connexion."
            );
        } finally {
            setLoading(false);
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    // -------- debounced auto-save (5s idle)
    const scheduleSave = useCallback((next) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setSaving(true);
            try {
                const res = await fetch(`${API}/${token}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(next),
                });
                if (res.ok) {
                    setSavedAt(new Date());
                }
            } finally {
                setSaving(false);
            }
        }, 900);
    }, [token]);

    const setField = useCallback((key, value) => {
        setData((prev) => {
            const next = { ...prev, [key]: value };
            scheduleSave(next);
            return next;
        });
    }, [scheduleSave]);

    const onDocUploaded = useCallback((key, doc) => {
        setRecord((prev) => ({
            ...prev,
            documents: { ...(prev.documents || {}), [key]: doc },
        }));
    }, []);
    const onDocRemoved = useCallback((key) => {
        setRecord((prev) => {
            const nextDocs = { ...(prev.documents || {}) };
            delete nextDocs[key];
            return { ...prev, documents: nextDocs };
        });
    }, []);

    // -------- computed state
    const documents = record?.documents || {};
    const isLocked = record?.is_locked || false;
    const errors = useMemo(() => fieldErrors(data), [data]);
    const ok = (keys) => keys.every((k) => data[k] && !errors[k]);
    const civilComplete = ok(["civilite", "last_name", "first_name", "birth_date", "nationality",
        "social_security_number", "birth_city", "birth_dept"]);
    const contactComplete = ok(["address_line1", "address_city", "address_postal_code",
        "email", "phone_mobile"]);
    const familyComplete = !!data.marital_status;
    // bankComplete requires the fields to PASS format validation, not just
    // be present. Combined with the live hint below the inputs, this
    // stops the candidate from hitting "Envoyer" with a typo.
    const bankComplete = !!(
        data.iban && isValidIban(data.iban)
        && data.bic && isValidBic(data.bic)
    );
    const docsComplete = DOCS.filter((d) => d.required).every((d) => documents[d.key]);
    const overallComplete = civilComplete && contactComplete && familyComplete && bankComplete && docsComplete;

    const dependents = useMemo(() => data.dependents || [], [data.dependents]);
    const addDependent = () => {
        setField("dependents", [...dependents, { last_name: "", first_name: "", birth_date: "", sex: "" }]);
    };
    const patchDependent = (idx, patch) => {
        const next = dependents.map((d, i) => (i === idx ? { ...d, ...patch } : d));
        setField("dependents", next);
    };
    const removeDependent = (idx) => {
        setField("dependents", dependents.filter((_, i) => i !== idx));
    };

    async function onSubmit() {
        setSubmitting(true);
        setSubmitError(null);
        try {
            // Flush any pending save
            if (debounceRef.current) clearTimeout(debounceRef.current);
            await fetch(`${API}/${token}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });
            const res = await fetch(`${API}/${token}/submit`, { method: "POST" });
            const j = await res.json();
            if (!res.ok) {
                setSubmitError(j.detail || { message: "Erreur inconnue" });
                return;
            }
            // Refresh to lock UI
            await load();
        } finally {
            setSubmitting(false);
        }
    }

    // -------- render
    if (loading) {
        return (
            <div className="min-h-screen bg-shariti-cream flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }
    if (loadError) {
        return (
            <div className="min-h-screen bg-shariti-cream flex flex-col items-center justify-center px-6" data-testid="onboarding-load-error">
                <AlertCircle className="h-10 w-10 text-shariti-orange mb-3" />
                <div className="text-lg font-black text-shariti-black text-center max-w-md">{loadError}</div>
                <button
                    onClick={() => { setLoading(true); load(); }}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white h-11 px-5 font-black text-xs uppercase tracking-widest hover:bg-shariti-black transition"
                    data-testid="onboarding-load-retry"
                >
                    Réessayer
                </button>
            </div>
        );
    }
    if (record?.error) {
        return (
            <div className="min-h-screen bg-shariti-cream flex flex-col items-center justify-center px-6">
                <AlertCircle className="h-10 w-10 text-shariti-red mb-3" />
                <div className="text-lg font-black text-shariti-black">Lien invalide ou expiré</div>
                <p className="text-sm font-medium text-slate-500 mt-1 text-center max-w-md">
                    Contactez votre référent Shariti pour recevoir un nouveau lien.
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-shariti-cream pb-40" data-testid="onboarding-page">
            {/* Hero */}
            <header className="bg-shariti-black text-white">
                <div className="max-w-4xl mx-auto px-5 md:px-8 py-8 md:py-12">
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange/20 text-shariti-orange px-3 py-1 text-[10px] font-black uppercase tracking-widest mb-4">
                        <Sparkles className="h-3 w-3" /> Dossier d'embauche
                    </div>
                    <h1 className="text-3xl md:text-5xl font-black tracking-tight">
                        Bienvenue chez <span className="text-shariti-orange">Shariti</span>,<br />
                        {record.candidate_first_name} 🧡
                    </h1>
                    <p className="mt-3 text-sm md:text-base text-white/80 font-medium max-w-2xl">
                        Complétez votre dossier en quelques minutes. Vos informations sont
                        sauvegardées automatiquement, vous pouvez revenir plus tard.
                    </p>
                    {record.poste_hint && (
                        <div className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-white/10 backdrop-blur border border-white/10 px-4 py-2.5 text-[13px] font-bold">
                            <UserRound className="h-4 w-4 text-shariti-orange" />
                            {record.poste_hint}
                        </div>
                    )}
                </div>
            </header>

            {/* Progress + save indicator (sticky) */}
            <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-shariti-gray/50">
                <div className="max-w-4xl mx-auto px-5 md:px-8 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                            {overallComplete ? "Prêt à envoyer" : "Progression"}
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-shariti-orange to-emerald-500 transition-all"
                                style={{ width: `${(
                                    (civilComplete ? 20 : 0)
                                    + (contactComplete ? 20 : 0)
                                    + (familyComplete ? 10 : 0)
                                    + (bankComplete ? 15 : 0)
                                    + (docsComplete ? 35 : 0)
                                )}%` }}
                            />
                        </div>
                    </div>
                    <div className="text-[10px] font-bold text-slate-500 shrink-0" data-testid="save-status">
                        {isLocked
                            ? <span className="inline-flex items-center gap-1 text-emerald-700"><Lock className="h-3 w-3" /> Verrouillé</span>
                            : saving
                                ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Enregistrement</span>
                                : savedAt
                                    ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="h-3 w-3" /> Sauvegardé</span>
                                    : <span>Auto-sauvegarde</span>
                        }
                    </div>
                </div>
            </div>

            {isLocked && (
                <div className="max-w-4xl mx-auto px-5 md:px-8 pt-6">
                    <div className="rounded-3xl bg-emerald-500 text-white p-5 flex items-start gap-3 shadow-lg" data-testid="locked-banner">
                        <CheckCircle2 className="h-6 w-6 shrink-0 mt-0.5" />
                        <div>
                            <div className="text-lg font-black">Dossier envoyé — merci {record.candidate_first_name} !</div>
                            <div className="text-sm font-medium text-white/90 mt-1">
                                Notre équipe RH le vérifie et prépare votre contrat. Vous recevrez
                                un nouvel email dès qu'il sera prêt à signer.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <main className="max-w-4xl mx-auto px-5 md:px-8 mt-6 space-y-4">
                {/* iter110 · Association mandataire — le candidat choisit qui il représentera
                    sur le terrain. Sert à générer le badge avec le bon logo/couleurs. */}
                <Section
                    title="Association mandataire"
                    icon={Sparkles}
                    complete={Boolean(data.association)}
                    testId="section-association"
                >
                    <div className="grid grid-cols-1 gap-3 pt-2">
                        <p className="text-xs text-slate-500 font-semibold">
                            Sélectionnez l'association pour laquelle vous ferez la collecte de dons.
                            Cette information sert à imprimer votre badge officiel.
                        </p>
                        <Select
                            label="Association"
                            value={data.association}
                            onChange={(v) => setField("association", v)}
                            required
                            locked={isLocked}
                            cols={12}
                            options={[
                                { value: "amnesty_international", label: "Amnesty International" },
                                { value: "ccfd_terre_solidaire", label: "CCFD Terre Solidaire" },
                                { value: "handicap_international", label: "Handicap International" },
                                { value: "chaine_espoir", label: "La Chaîne de l'Espoir" },
                            ]}
                            testId="field-association"
                        />
                    </div>
                </Section>

                {/* ─── État civil */}
                <Section title="État civil" icon={UserRound} complete={civilComplete} testId="section-civil">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 pt-2">
                        <Select label="Civilité" value={data.civilite} onChange={(v) => setField("civilite", v)} required locked={isLocked} cols={4}
                            options={[{ value: "Mr", label: "Monsieur" }, { value: "Mme", label: "Madame" }]}
                            testId="field-civilite" />
                        <Field label="Nom" value={data.last_name} onChange={(v) => setField("last_name", v)} required testId="field-last-name" locked={isLocked} cols={4} error={errors.last_name} />
                        <Field label="Prénom(s)" value={data.first_name} onChange={(v) => setField("first_name", v)} required testId="field-first-name" locked={isLocked} cols={4} error={errors.first_name} />
                        <Field label="Nom de jeune fille" value={data.maiden_name} onChange={(v) => setField("maiden_name", v)} testId="field-maiden" locked={isLocked} cols={6} error={errors.maiden_name} />
                        <Field label="Date de naissance" type="date" value={data.birth_date} onChange={(v) => setField("birth_date", v)} required testId="field-birth-date" locked={isLocked} cols={6} />
                        <Field label="Ville de naissance" value={data.birth_city} onChange={(v) => setField("birth_city", v)} required testId="field-birth-city" locked={isLocked} cols={6} error={errors.birth_city} />
                        <Field label="Dépt. de naissance" value={data.birth_dept} onChange={(v) => setField("birth_dept", v)} required placeholder="ex. 75" testId="field-birth-dept" locked={isLocked} cols={3} />
                        <Field label="Pays" value={data.birth_country} onChange={(v) => setField("birth_country", v)} testId="field-birth-country" locked={isLocked} cols={3} />
                        <Field label="Nationalité" value={data.nationality} onChange={(v) => setField("nationality", v)} required testId="field-nationality" locked={isLocked} cols={6} />
                        <Field label="N° de sécurité sociale" value={data.social_security_number} onChange={(v) => setField("social_security_number", v)} required placeholder="15 chiffres" testId="field-ssn" locked={isLocked} cols={6} error={errors.social_security_number} />
                    </div>
                </Section>

                {/* ─── Étrangers — repliable, hidden par défaut si non renseigné */}
                <Section title="Personnes étrangères (optionnel)" icon={IdCard} complete={false} defaultOpen={!!data.foreign_status} testId="section-foreign">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 pt-2">
                        <Select label="Statut" value={data.foreign_status} onChange={(v) => setField("foreign_status", v)} locked={isLocked} cols={4}
                            options={[
                                { value: "etudiant", label: "Étudiant(e)" },
                                { value: "handicap", label: "Handicap" },
                                { value: "autre", label: "Autre" },
                            ]}
                            testId="field-foreign-status" />
                        <Field label="N° titre de séjour / travail" value={data.permit_number} onChange={(v) => setField("permit_number", v)} testId="field-permit-num" locked={isLocked} cols={4} />
                        <Field label="Lieu de délivrance" value={data.permit_issue_place} onChange={(v) => setField("permit_issue_place", v)} testId="field-permit-place" locked={isLocked} cols={4} />
                        <Field label="Date d'expiration" type="date" value={data.permit_expiry} onChange={(v) => setField("permit_expiry", v)} testId="field-permit-expiry" locked={isLocked} cols={6} />
                        <Field label="Type" value={data.permit_type} onChange={(v) => setField("permit_type", v)} testId="field-permit-type" locked={isLocked} cols={6} />
                    </div>
                </Section>

                {/* ─── Coordonnées */}
                <Section title="Coordonnées" icon={MapPin} complete={contactComplete} testId="section-contact">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 pt-2">
                        <Field label="Adresse (n° et rue)" value={data.address_line1} onChange={(v) => setField("address_line1", v)} required placeholder="ex. 12 rue des Lilas" testId="field-address" locked={isLocked} cols={12} error={errors.address_line1} />
                        <Field label="Ville" value={data.address_city} onChange={(v) => setField("address_city", v)} required placeholder="ex. Bordeaux" testId="field-city" locked={isLocked} cols={8} error={errors.address_city} />
                        <Field label="Code postal" value={data.address_postal_code} onChange={(v) => setField("address_postal_code", v)} required placeholder="ex. 33800" testId="field-zip" locked={isLocked} cols={4} error={errors.address_postal_code} />
                        <Field label="Email" type="email" value={data.email} onChange={(v) => setField("email", v)} required placeholder="ex. prenom.nom@gmail.com" testId="field-email" locked={isLocked} cols={6} error={errors.email} />
                        <Field label="Téléphone mobile" value={data.phone_mobile} onChange={(v) => setField("phone_mobile", v)} required placeholder="ex. 06 12 34 56 78" testId="field-phone-mobile" locked={isLocked} cols={6} error={errors.phone_mobile} />
                        <Field label="Téléphone fixe (optionnel)" value={data.phone_landline} onChange={(v) => setField("phone_landline", v)} testId="field-phone-landline" locked={isLocked} cols={6} />
                    </div>
                    <div className="mt-6 rounded-2xl bg-shariti-cream/60 border border-shariti-gray/40 p-4">
                        <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-3 inline-flex items-center gap-1.5">
                            <Phone className="h-3 w-3" /> Personne à prévenir en cas d'accident
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                            <Field label="Nom" value={data.emergency_name} onChange={(v) => setField("emergency_name", v)} testId="field-emergency-name" locked={isLocked} cols={4} />
                            <Field label="Mobile" value={data.emergency_phone_mobile} onChange={(v) => setField("emergency_phone_mobile", v)} testId="field-emergency-mobile" locked={isLocked} cols={4} />
                            <Field label="Fixe" value={data.emergency_phone_landline} onChange={(v) => setField("emergency_phone_landline", v)} testId="field-emergency-landline" locked={isLocked} cols={4} />
                        </div>
                    </div>
                </Section>

                {/* ─── Situation familiale */}
                <Section title="Situation familiale" icon={Users} complete={familyComplete} testId="section-family">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 pt-2">
                        <Select label="Statut" value={data.marital_status} onChange={(v) => setField("marital_status", v)} required locked={isLocked} cols={6}
                            options={[
                                { value: "celibataire", label: "Célibataire" },
                                { value: "marie", label: "Marié(e)" },
                                { value: "pacse", label: "Pacsé(e)" },
                                { value: "divorce", label: "Divorcé(e)" },
                                { value: "veuf", label: "Veuf / Veuve" },
                                { value: "separe", label: "Séparé(e)" },
                                { value: "retraite", label: "Retraité(e)" },
                                { value: "autre", label: "Autre" },
                            ]}
                            testId="field-marital-status" />
                        <Field label="Nom et prénom du conjoint" value={data.spouse_name} onChange={(v) => setField("spouse_name", v)} testId="field-spouse" locked={isLocked} cols={6} />
                        <Field label="Nb adultes à charge" type="number" value={data.nb_adults_dependent ?? 0} onChange={(v) => setField("nb_adults_dependent", parseInt(v || 0, 10))} testId="field-nb-adults" locked={isLocked} cols={6} />
                        <Field label="Nb enfants à charge" type="number" value={data.nb_children_dependent ?? 0} onChange={(v) => setField("nb_children_dependent", parseInt(v || 0, 10))} testId="field-nb-children" locked={isLocked} cols={6} />
                    </div>

                    <div className="mt-5">
                        <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">
                            Personnes à charge (détail)
                        </div>
                        <div className="space-y-2">
                            {dependents.map((d, idx) => (
                                <div key={idx} className="rounded-2xl bg-shariti-cream/60 border border-shariti-gray/40 p-3 grid grid-cols-2 sm:grid-cols-12 gap-2" data-testid={`dependent-${idx}`}>
                                    <Field label="Nom" value={d.last_name} onChange={(v) => patchDependent(idx, { last_name: v })} testId={`dep-${idx}-last`} locked={isLocked} cols={3} />
                                    <Field label="Prénom" value={d.first_name} onChange={(v) => patchDependent(idx, { first_name: v })} testId={`dep-${idx}-first`} locked={isLocked} cols={3} />
                                    <Field label="Né(e) le" type="date" value={d.birth_date} onChange={(v) => patchDependent(idx, { birth_date: v })} testId={`dep-${idx}-birth`} locked={isLocked} cols={3} />
                                    <Select label="Sexe" value={d.sex} onChange={(v) => patchDependent(idx, { sex: v })} locked={isLocked} cols={2}
                                        options={[{ value: "M", label: "M" }, { value: "F", label: "F" }]}
                                        testId={`dep-${idx}-sex`} />
                                    {!isLocked && (
                                        <button type="button" onClick={() => removeDependent(idx)} className="sm:col-span-1 text-red-600 hover:underline text-xs font-bold self-end pb-3" data-testid={`dep-${idx}-remove`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {!isLocked && (
                                <button type="button" onClick={addDependent}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-dashed border-shariti-orange/50 text-shariti-orange px-4 h-9 text-[11px] font-black uppercase tracking-widest hover:bg-shariti-orange-soft transition"
                                    data-testid="add-dependent">
                                    <Plus className="h-3 w-3" /> Ajouter une personne
                                </button>
                            )}
                        </div>
                    </div>
                </Section>

                {/* ─── Coordonnées bancaires (obligatoires PayFit) */}
                <Section title="Coordonnées bancaires" icon={Banknote} complete={bankComplete} testId="section-bank">
                    <p className="text-[13px] font-medium text-slate-600 mt-2 mb-3">
                        Ces informations serviront au versement de votre salaire. Elles se
                        trouvent sur votre RIB.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 pt-2">
                        <Field
                            label="IBAN"
                            value={data.iban}
                            onChange={(v) => setField("iban", (v || "").toUpperCase().replace(/\s+/g, ""))}
                            required
                            placeholder="FR76 3000 4000 5000 6000 7000 189"
                            testId="field-iban"
                            locked={isLocked}
                            cols={8}
                        />
                        <Field
                            label="BIC / SWIFT"
                            value={data.bic}
                            onChange={(v) => setField("bic", (v || "").toUpperCase().replace(/\s+/g, ""))}
                            required
                            placeholder="BNPAFRPPXXX"
                            testId="field-bic"
                            locked={isLocked}
                            cols={4}
                        />
                        <div className="sm:col-span-8 -mt-2">
                            <BankValidityHint
                                value={data.iban}
                                validate={isValidIban}
                                labelOk="IBAN valide (mod-97 OK)"
                                labelKo="Format IBAN invalide — vérifiez la saisie"
                                testId="iban-live-hint"
                            />
                        </div>
                        <div className="sm:col-span-4 -mt-2">
                            <BankValidityHint
                                value={data.bic}
                                validate={isValidBic}
                                labelOk="BIC valide"
                                labelKo="Format BIC invalide (8 ou 11 caractères)"
                                testId="bic-live-hint"
                            />
                        </div>
                    </div>
                </Section>

                {/* ─── Documents */}
                <Section title="Documents administratifs" icon={FileText} complete={docsComplete} testId="section-docs">
                    <p className="text-[13px] font-medium text-slate-600 mt-2 mb-4">
                        Photos ou PDF, max 10 Mo par fichier. Les 5 premiers documents sont
                        obligatoires. Les 4 suivants dépendent de votre situation.
                    </p>
                    <div className="space-y-2.5">
                        {DOCS.map((doc) => (
                            <DocRow
                                key={doc.key}
                                token={token}
                                doc={doc}
                                uploaded={documents[doc.key]}
                                onUploaded={onDocUploaded}
                                onRemoved={onDocRemoved}
                                locked={isLocked}
                            />
                        ))}
                    </div>
                </Section>

                {/* ─── Submit */}
                {!isLocked && (
                    <div className="pt-4">
                        {submitError && (
                            <div className="mb-3 rounded-2xl bg-red-50 border-2 border-red-200 p-4" data-testid="submit-error">
                                <div className="text-sm font-black text-red-700 flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4" /> {submitError.message || "Impossible d'envoyer"}
                                </div>
                                {submitError.missing_fields?.length > 0 && (
                                    <div className="mt-1 text-xs font-semibold text-red-700">
                                        Champs manquants : {submitError.missing_fields.join(", ")}
                                    </div>
                                )}
                                {submitError.invalid_fields?.length > 0 && (
                                    <div className="mt-1 text-xs font-semibold text-red-700" data-testid="submit-error-invalid">
                                        {submitError.invalid_messages && Object.keys(submitError.invalid_messages).length > 0
                                            ? Object.values(submitError.invalid_messages).map((m) => <div key={m}>• {m}</div>)
                                            : <>Champs invalides : {submitError.invalid_fields.join(", ")} — vérifiez le format (ex. IBAN mod-97).</>}
                                    </div>
                                )}
                                {submitError.missing_docs?.length > 0 && (
                                    <div className="mt-1 text-xs font-semibold text-red-700">
                                        Documents manquants : {submitError.missing_docs.join(", ")}
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={onSubmit}
                            disabled={!overallComplete || submitting}
                            data-testid="submit-btn"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-shariti-orange text-white h-14 font-black text-sm uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed hover:bg-shariti-black transition shadow-btn"
                        >
                            {submitting
                                ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi</>
                                : <><Send className="h-4 w-4" /> Envoyer mon dossier</>
                            }
                        </button>
                        {!overallComplete && (
                            <div className="mt-3 text-center text-[11px] font-semibold text-slate-500">
                                Complétez toutes les sections et documents obligatoires pour envoyer votre dossier.
                            </div>
                        )}
                    </div>
                )}
            </main>

            <footer className="mt-16 text-center text-[11px] font-medium text-slate-500">
                © 2026 Shariti · Vos données restent confidentielles et sont utilisées uniquement pour votre embauche.
            </footer>
        </div>
    );
}
